/**
 * GenerationsStoryModal — full-screen modal that opens from the
 * DamalsHeute tile on /heute. Fetches the 3-paragraph Gemini-generated
 * climate narrative for one of the 3 LANDING_ANCHORS, with copy + share
 * buttons so it can land on Instagram in two taps.
 *
 * Architecture:
 *   - Lazy-loaded — the API call only fires once the user opens the modal.
 *   - 24 h backend cache means subsequent opens within the same month are
 *     instant.
 *   - Share fallback: navigator.share → navigator.clipboard.writeText.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, ShareNetwork, Copy, CircleNotch, Sparkle, Clock,
  ArrowUp, ArrowDown, Newspaper, DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { fetchGenerationsStory } from "@/lib/api";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function deltaTone(deltaC) {
  if (deltaC == null) return "#F5F5F5";
  if (deltaC >= 1.0) return "#FF5500";
  if (deltaC >= 0.3) return "#FFC24B";
  if (deltaC <= -0.3) return "#8B7CF6";
  return "#00FA9A";
}

function Paragraph({ eyebrow, text, accent, testid }) {
  return (
    <div className="border-l-4 pl-4 sm:pl-5" style={{ borderColor: accent }} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-mono font-semibold" style={{ color: accent }}>
        {eyebrow}
      </div>
      <p className="mt-1.5 text-[15px] sm:text-base text-white leading-relaxed">
        {text}
      </p>
    </div>
  );
}

/**
 * DeltaScale — visual range from "kühler" to "Hitze-Welle" so the user
 * immediately understands where this Δ sits on the climate-shift spectrum.
 */
function DeltaScale({ delta }) {
  // Map [-2 … +4] °C to 0…100 % on the bar.
  const min = -2;
  const max = 4;
  const pct = Math.max(0, Math.min(100, ((delta - min) / (max - min)) * 100));

  const ticks = [
    { v: -2, label: "kühler" },
    { v: 0, label: "stabil" },
    { v: 1.5, label: "spürbar" },
    { v: 3, label: "Hitze" },
  ];

  return (
    <div className="bg-[#121212] border border-[#D9A87C]/40 p-4 rounded-sm" data-testid="story-delta-scale">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#D9A87C] mb-3">
        Klimawandel-Skala für deine Region
      </div>
      <div
        className="relative h-2.5 rounded-full"
        style={{
          background: "linear-gradient(to right, #3D2A78 0%, #497B94 22%, #7BB661 38%, #FFD700 55%, #FFC24B 72%, #C05A3E 100%)",
        }}
      >
        {/* Pointer */}
        <motion.div
          className="absolute -top-1.5 -translate-x-1/2"
          initial={{ left: "50%", opacity: 0 }}
          animate={{ left: `${pct}%`, opacity: 1 }}
          transition={{ type: "spring", stiffness: 120, damping: 20, delay: 0.2 }}
        >
          <div className="w-[3px] h-[22px] bg-white/10 rounded-full" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/10 -mt-1 mx-auto border-2 border-white" />
        </motion.div>
        {/* Tick marks (subtle) */}
        {ticks.map((t) => {
          const tpct = ((t.v - min) / (max - min)) * 100;
          return (
            <div
              key={t.v}
              className="absolute top-3 -translate-x-1/2 text-[9px] font-mono uppercase tracking-widest text-zinc-400 whitespace-nowrap"
              style={{ left: `${tpct}%`, top: "16px" }}
            >
              {t.label}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-zinc-400 mt-7 leading-relaxed">
        Der schwarze Pointer zeigt, <strong className="text-white">wo dein {delta >= 0 ? "Plus" : "Minus"} von {(delta > 0 ? "+" : "") + delta.toFixed(1)} °C</strong> liegt:{" "}
        {delta < 0.3 ? "klimatisch stabil" :
          delta < 1.0 ? "spürbar wärmer — Vegetationsperiode beginnt früher" :
          delta < 2.0 ? "deutlich wärmer — Aussaat- und Erntetermine verschieben sich" :
          "Hitze-Bereich — neue Sorten und Bewässerungs-Strategien werden nötig"}.
      </p>
    </div>
  );
}

export default function GenerationsStoryModal({ open, anchor, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !anchor?.anchor_id) return;
    setLoading(true);
    setError(null);
    fetchGenerationsStory(anchor.anchor_id)
      .then((r) => {
        if (r.ok) setData(r);
        else setError(r.fallback || "Die Geschichte kann gerade nicht erzeugt werden.");
      })
      .catch(() => setError("Verbindung zum Server fehlgeschlagen."))
      .finally(() => setLoading(false));
  }, [open, anchor?.anchor_id]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const handleShare = async () => {
    if (!data?.share_text) return;
    const url = `${window.location.origin}/heritage`;
    const payload = {
      title: `Damals vs. Heute · ${data.anchor_label}`,
      text: data.share_text,
      url,
    };
    try {
      if (navigator.share && navigator.canShare?.(payload)) {
        await navigator.share(payload);
        toast.success("Geteilt ✓");
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(`${data.share_text}\n${url}`);
      toast.success("In Zwischenablage kopiert · in Instagram einfügen!");
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const handleCopyOnly = async () => {
    if (!data?.share_text) return;
    try {
      await navigator.clipboard.writeText(data.share_text);
      toast.success("Text kopiert ✓");
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const delta = anchor?.delta_temp_c;
  const Arrow = (delta ?? 0) >= 0 ? ArrowUp : ArrowDown;
  const t = deltaTone(delta);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
          onClick={onClose}
          data-testid="generations-story-modal"
        >
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0A0A0A] w-full max-w-2xl my-auto sm:rounded-sm relative shadow-2xl"
          >
            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 p-2 text-zinc-400 hover:text-white z-10"
              aria-label="Schließen"
              data-testid="modal-close"
            >
              <X size={20} weight="bold" />
            </button>

            {/* Header */}
            <header
              className="px-5 sm:px-7 pt-6 pb-5 relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #16130D 0%, #FFFFFF 80%)",
                borderBottom: "1px solid rgba(122,74,45,0.15)",
              }}
            >
              <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-[#D9A87C] flex items-center gap-2">
                <Sparkle size={11} weight="fill" /> Generationen-Geschichte
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 leading-tight">
                {anchor?.label || "Region"}
              </h2>
              {delta != null && (
                <div className="mt-2 flex items-center gap-2 flex-wrap text-sm">
                  <span className="inline-flex items-baseline gap-0.5 font-bold" style={{ color: t }}>
                    <Arrow size={14} weight="bold" />
                    {(delta > 0 ? "+" : "") + delta.toFixed(1)}
                    <span className="text-xs opacity-70 ml-0.5">°C</span>
                  </span>
                  <span className="text-white/65">gegenüber vor 30 Jahren</span>
                </div>
              )}
              {anchor?.today_temp_c != null && anchor?.then_temp_c != null && (
                <div className="text-[11px] font-mono text-white/55 mt-1">
                  heute Ø {anchor.today_temp_c.toFixed(1)} °C · damals {anchor.then_temp_c.toFixed(1)} °C · ERA5-Archiv (ECMWF)
                </div>
              )}
            </header>

            {/* Body */}
            <div className="px-5 sm:px-7 py-5 sm:py-7 space-y-5">
              {loading && (
                <div className="text-center py-12" data-testid="modal-loading">
                  <CircleNotch size={28} className="animate-spin text-[#D9A87C] mx-auto" />
                  <p className="text-sm text-zinc-400 mt-3">
                    Generationen-Geschichte wird geschrieben …
                  </p>
                </div>
              )}

              {!loading && error && (
                <div
                  className="border-l-4 border-[#FF5500]/40 bg-[#121212] p-4 text-sm text-white"
                  data-testid="modal-error"
                >
                  {error}
                </div>
              )}

              {!loading && !error && data?.story && (
                <>
                  {/* Δ-Visualisierung: where on the climate-shift spectrum we are */}
                  {delta != null && <DeltaScale delta={delta} />}
                  <Paragraph
                    eyebrow="DAMALS"
                    text={data.story.damals}
                    accent="#D9A87C"
                    testid="story-damals"
                  />
                  <Paragraph
                    eyebrow="HEUTE"
                    text={data.story.heute}
                    accent="#FF5500"
                    testid="story-heute"
                  />
                  <Paragraph
                    eyebrow="WAS DAS BEDEUTET"
                    text={data.story.bedeutet}
                    accent="#00FA9A"
                    testid="story-bedeutet"
                  />
                  <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 pt-2 border-t border-white/15">
                    Geschichte erstellt mit Gemini · Datengrundlage: ERA5-Archiv (ECMWF) · keine Beratung
                  </p>
                </>
              )}
            </div>

            {/* Footer / actions */}
            {data?.story && (
              <footer className="px-5 sm:px-7 pb-6 pt-3 border-t border-white/15 bg-[#121212] sm:rounded-b-sm space-y-2.5">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleShare}
                    className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 bg-[#F5F5F5] text-[#0A0A0A] px-4 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-[#7A4A2D] transition-colors rounded-sm"
                    data-testid="story-share-btn"
                  >
                    <ShareNetwork size={14} weight="bold" /> Auf Instagram teilen
                  </button>
                  <a
                    href={`${API}/api/heritage/generations-story/${anchor.anchor_id}/poster.png`}
                    download={`bodenblick-damals-heute-${anchor.anchor_id}.png`}
                    className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 border-2 border-[#D9A87C]/40 text-[#D9A87C] px-4 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-[#7A4A2D] hover:text-white transition-colors rounded-sm"
                    data-testid="story-poster-btn"
                  >
                    <DownloadSimple size={14} weight="bold" /> Als Bild speichern
                  </a>
                  <button
                    type="button"
                    onClick={handleCopyOnly}
                    className="inline-flex items-center justify-center gap-2 border border-white/15 px-4 py-3 text-sm font-medium uppercase tracking-widest hover:bg-white/5 transition-colors rounded-sm"
                    data-testid="story-copy-btn"
                  >
                    <Copy size={14} weight="bold" />
                    <span className="sr-only sm:not-sr-only">Text kopieren</span>
                  </button>
                </div>
                <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                  <span>1080 × 1920 · bereit für Instagram Story</span>
                  <Link
                    to="/heritage"
                    onClick={onClose}
                    className="inline-flex items-center gap-1 hover:text-white hover:underline"
                    data-testid="story-heritage-link"
                  >
                    <Clock size={11} /> Opas Wetter →
                  </Link>
                </div>
              </footer>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
